import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  Grid,
  Avatar,
  Paper
} from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import HomeIcon from '@mui/icons-material/Home';
import WcIcon from '@mui/icons-material/Wc';
import CakeIcon from '@mui/icons-material/Cake';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import StarIcon from '@mui/icons-material/Star';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ReceiptIcon from '@mui/icons-material/Receipt';

const SimpleCustomerModal = ({ open, onClose, customer }) => {
  if (!customer) return null;

  // Bảng màu đa dạng cho các icon và label
  const colors = {
    header: '#3f51b5',      // Xanh dương đậm
    email: '#e91e63',       // Hồng
    phone: '#009688',       // Xanh ngọc
    address: '#ff5722',     // Cam đỏ
    gender: '#673ab7',      // Tím
    birthDate: '#2196f3',   // Xanh dương
    totalPrice: '#f44336',  // Đỏ
    points: '#4caf50',      // Xanh lá
    membership: '#ffc107',  // Vàng
    joinDate: '#795548',    // Nâu
    orderCount: '#607d8b'   // Xanh xám
  };

  // Tạo component InfoCard để tái sử dụng
  const InfoCard = ({ icon, color, label, value }) => (
    <Paper 
      elevation={0} 
      sx={{ 
        p: 2, 
        bgcolor: '#f5f5f5', 
        borderRadius: 2,
        height: '100%',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        {React.cloneElement(icon, { sx: { color: color, mr: 1, fontSize: 20 } })}
        <Typography variant="body2" sx={{ fontWeight: 600, color: color }}>
          {label}
        </Typography>
      </Box>
      <Typography 
        variant="body1" 
        sx={{ 
          fontWeight: 500,
          wordBreak: 'break-word',
          overflowWrap: 'break-word'
        }}
      >
        {value || "N/A"}
      </Typography>
    </Paper>
  );

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
          overflow: 'hidden'
        }
      }}
    >
      <DialogTitle 
        sx={{ 
          bgcolor: colors.header, 
          color: 'white',
          py: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Thông tin khách hàng
        </Typography>
        <IconButton 
          onClick={onClose}
          sx={{ color: 'white' }}
          size="small"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {/* Header với avatar */}
        <Box sx={{ 
          p: 2, 
          display: 'flex', 
          alignItems: 'center',
          borderBottom: '1px solid #eee',
          bgcolor: '#f9f9f9',
          height: 'auto',
          minHeight: '80px'
        }}>
          <Avatar 
            sx={{ 
              width: 50, 
              height: 50, 
              bgcolor: colors.header,
              mr: 2
            }}
          >
            {customer.name?.charAt(0) || 'K'}
          </Avatar>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: '1rem' }}>
              {customer.name || "N/A"}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Mã khách hàng: {customer.id || "N/A"}
            </Typography>
          </Box>
        </Box>

        {/* Thông tin cá nhân */}
        <Box sx={{ p: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2, color: colors.header }}>
            Thông tin cá nhân
          </Typography>
          
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <InfoCard 
                icon={<PhoneIcon />} 
                color={colors.phone} 
                label="Số điện thoại" 
                value={customer.phone} 
              />
            </Grid>
            
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <InfoCard 
                icon={<HomeIcon />} 
                color={colors.address} 
                label="Địa chỉ" 
                value={customer.address} 
              />
            </Grid>
            
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <InfoCard 
                icon={<WcIcon />} 
                color={colors.gender} 
                label="Giới tính" 
                value={customer.gender} 
              />
            </Grid>
            
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <InfoCard 
                icon={<CakeIcon />} 
                color={colors.birthDate} 
                label="Ngày sinh" 
                value={customer.birthDate} 
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <InfoCard 
                icon={<EmailIcon />} 
                color={colors.email} 
                label="Email" 
                value={customer.email} 
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <InfoCard 
                icon={<CalendarTodayIcon />} 
                color={colors.joinDate} 
                label="Ngày tham gia" 
                value={customer.dateJoined || "N/A"} 
              />
            </Grid>
          </Grid>
        </Box>
        
        {/* Thông tin mua hàng */}
        <Box sx={{ p: 3, borderTop: '1px solid #eee' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2, color: colors.header }}>
            Thông tin mua hàng
          </Typography>
          
          <Grid container spacing={2}>
            
            
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <InfoCard 
                icon={<StarIcon />} 
                color={colors.membership} 
                label="Cấp độ thành viên" 
                value={customer.membershipLevel || "Đồng"} 
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <InfoCard 
                icon={<EmojiEventsIcon />} 
                color={colors.points} 
                label="Điểm tích lũy" 
                value={customer.pointaccumulated || "0"} 
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <InfoCard 
                icon={<ShoppingBagIcon />} 
                color={colors.totalPrice} 
                label="Tổng chi tiêu" 
                value={customer.totalprice || "$0"} 
              />
            </Grid>
            
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <InfoCard 
                icon={<ReceiptIcon />} 
                color={colors.orderCount} 
                label="Tổng số đơn hàng" 
                value={customer.orderCount || "0"} 
              />
            </Grid>
            
            
            
            
          </Grid>
        </Box>
      </DialogContent>
      
      <DialogActions sx={{ p: 2, justifyContent: 'center' }}>
        <Button 
          variant="contained" 
          onClick={onClose}
          sx={{ 
            borderRadius: 8, 
            px: 4,
            py: 1,
            fontWeight: 'bold',
            minWidth: 120,
            bgcolor: colors.header,
            '&:hover': {
              bgcolor: '#303f9f'
            }
          }}
        >
          ĐÓNG
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SimpleCustomerModal;















